import React, { useEffect, useState, useRef, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  X, Phone, Mail, Tag, MessageCircle, Instagram, Globe,
  Pencil, Trash2, Plus, ListTodo, CheckCircle2, Circle, Hash, StickyNote, Check, Gift,
} from "lucide-react";
import MessageBubble from "./MessageBubble";
import InputArea from "./InputArea";
import QuickActions from "./QuickActions";
import EditLeadModal from "./EditLeadModal";
import HelpTooltip from "../shared/HelpTooltip";
import api, { absoluteMediaUrl } from "../../lib/api";
import { useWS } from "../../context/WebSocketContext";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const CHANNEL_ICONS = {
  whatsapp: { icon: MessageCircle, color: "text-[#25D366]" },
  instagram: { icon: Instagram, color: "text-pink-400" },
  email: { icon: Mail, color: "text-cyan-400" },
  facebook: { icon: Globe, color: "text-blue-400" },
};

const DEFAULT_TASK_TYPE_OPTIONS = [
  { value: "follow-up", label: "Acompanhar" },
  { value: "meeting", label: "Reunião" },
  { value: "proposal", label: "Proposta" },
  { value: "call", label: "Ligação" },
];

const ACTIVITY_LABELS = {
  note: "Nota",
  meeting: "Reunião",
  call: "Ligação",
  proposal: "Proposta",
  "follow-up": "Acompanhamento",
  reminder: "Lembrete futuro",
};

function formatActivityLabel(task, taskTypeLabels = {}) {
  if (task?.kind === "note") return ACTIVITY_LABELS.note;
  return task?.task_type_label || taskTypeLabels[task?.task_type] || ACTIVITY_LABELS[task?.task_type] || "Tarefa";
}

function formatActivityTimestamp(task) {
  const source = task?.due_at || task?.created_at;
  if (!source) return null;
  return new Date(source).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatPhoneDigits(value) {
  const source = String(value || "").split("@")[0];
  const digits = source.replace(/\D/g, "");
  if (!digits) return null;
  const normalized = digits.startsWith("55") ? digits : digits.length >= 10 ? `55${digits}` : digits;
  if (!normalized.startsWith("55") || normalized.length < 12) return null;
  const ddd = normalized.slice(2, 4);
  const local = normalized.slice(4);
  if (local.length === 9) return `+55 ${ddd} ${local.slice(0, 5)}-${local.slice(5)}`;
  if (local.length === 8) return `+55 ${ddd} ${local.slice(0, 4)}-${local.slice(4)}`;
  return `+${normalized}`;
}

function formatChannelPhone(channel) {
  return channel?.formattedPhone || formatPhoneDigits(channel?.rawPhone || channel?.number || channel?.jid || channel?.identifier);
}

function compactChannelLabel(channel) {
  const label = channel?.displayName || channel?.name || "Canal";
  if (channel?.type === "instagram" || String(channel?.id || "").startsWith("instagram:")) {
    return label.replace(/^Instagram Direct\s*-\s*/i, "INSTAGRAM DIRECT - ").toUpperCase();
  }
  return label.replace(/^WhatsApp Nativo\s*-\s*/i, "WHATSAPP NATIVO - ").toUpperCase();
}

function isInstagramLead(lead) {
  return Boolean(
    lead?.channel === "instagram" ||
    lead?.provider === "meta" && (lead?.source === "instagram_direct" || lead?.last_message_channel === "instagram") ||
    lead?.source === "instagram_direct" ||
    lead?.last_message_channel === "instagram" ||
    lead?.instagram_user_id
  );
}

export default function ChatPanel({ lead, onClose, onLeadUpdate, onLeadDelete, initialPrefillMessage, onInitialPrefillConsumed }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [activityItems, setActivityItems] = useState([]);
  const [savingActivity, setSavingActivity] = useState(false);
  const [updatingTaskIds, setUpdatingTaskIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [channels, setChannels] = useState(null);
  const [responseChannels, setResponseChannels] = useState([]);
  const [selectedResponseChannelId, setSelectedResponseChannelId] = useState("");
  const [loadingResponseChannels, setLoadingResponseChannels] = useState(false);
  const [team, setTeam] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [showTagInput, setShowTagInput] = useState(false);
  const [showActivities, setShowActivities] = useState(false);
  const [actionMode, setActionMode] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [actionTitle, setActionTitle] = useState("");
  const [actionDueAt, setActionDueAt] = useState("");
  const [actionType, setActionType] = useState("follow-up");
  const [taskTypes, setTaskTypes] = useState(DEFAULT_TASK_TYPE_OPTIONS);
  const [actionAssignee, setActionAssignee] = useState("me");
  const [reminderRecipient, setReminderRecipient] = useState("");
  const [reminderMode, setReminderMode] = useState("relative");
  const [reminderDate, setReminderDate] = useState("");
  const [reminderRelativeValue, setReminderRelativeValue] = useState(3);
  const [reminderRelativeUnit, setReminderRelativeUnit] = useState("months");
  const scrollRef = useRef(null);
  const activityScrollRef = useRef(null);
  const savingActivityRef = useRef(false);
  const { on } = useWS();

  useEffect(() => {
    Promise.all([
      api.get("/channels").catch(() => ({ data: null })),
      api.get("/team").catch(() => ({ data: [] })),
      api.get("/task-types").catch(() => ({ data: [] })),
    ]).then(([channelsRes, teamRes, taskTypesRes]) => {
      setChannels(channelsRes?.data || null);
      setTeam(Array.isArray(teamRes?.data) ? teamRes.data : []);
      const options = Array.isArray(taskTypesRes?.data)
        ? taskTypesRes.data.filter((item) => item?.active !== false).map((item) => ({ value: item.id, label: item.name }))
        : [];
      if (options.length) {
        setTaskTypes(options);
        setActionType((current) => options.find((item) => item.value === current) ? current : options[0].value);
      }
    });
  }, []);

  const loadMessages = useCallback(async (leadId) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/messages/lead/${leadId}`);
      setMessages(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    if (lead?.id) loadMessages(lead.id);
  }, [lead?.id, loadMessages]);

  useEffect(() => {
    if (!lead?.id) return;
    let cancelled = false;
    setLoadingResponseChannels(true);
    api.get("/response-channels", { params: { lead_id: lead.id } })
      .then(({ data }) => {
        if (cancelled) return;
        const list = Array.isArray(data?.channels) ? data.channels : [];
        const instagramChannel = list.find((item) => item?.type === "instagram" || String(item?.id || "").startsWith("instagram:"));
        const defaultId = isInstagramLead(lead) && instagramChannel
          ? instagramChannel.id
          : data?.defaultChannelId || list[0]?.id || "";
        setResponseChannels(list);
        setSelectedResponseChannelId(defaultId);
      })
      .catch(() => {
        if (cancelled) return;
        setResponseChannels([]);
        setSelectedResponseChannelId("");
      })
      .finally(() => {
        if (!cancelled) setLoadingResponseChannels(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lead]);

  useEffect(() => {
    setActivityItems(Array.isArray(lead?.tasks) ? lead.tasks : []);
  }, [lead?.id, lead?.tasks]);

  // Mensagem sugerida vinda de fora (ex: card de Task na tela /tasks, via
  // navegação com location.state) — preenche o campo de digitação uma única
  // vez e avisa o pai pra limpar o state, evitando reaplicar em trocas
  // de lead subsequentes.
  useEffect(() => {
    if (lead?.id && initialPrefillMessage) {
      setPrefill(initialPrefillMessage);
      onInitialPrefillConsumed?.();
    }
  }, [lead?.id, initialPrefillMessage, onInitialPrefillConsumed]);

  useEffect(() => {
    if (!lead?.id) return;
    const addMsg = (payload) => {
      if (payload?.lead_id === lead.id && payload?.message) {
        setMessages((prev) => {
          if (prev.find((m) => m.id === payload.message.id)) return prev;
          return [...prev, payload.message];
        });
      }
    };
    const syncLead = (payload) => {
      if (payload?.lead?.id === lead.id) {
        setActivityItems(Array.isArray(payload?.lead?.tasks) ? payload.lead.tasks : []);
        onLeadUpdate?.(payload.lead);
      }
    };
    const un1 = on("message.received", addMsg);
    const un2 = on("message.sent", addMsg);
    const un3 = on("lead.updated", syncLead);
    return () => {
      un1?.();
      un2?.();
      un3?.();
    };
  }, [lead?.id, on, onLeadUpdate]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (showActivities && activityScrollRef.current) {
      activityScrollRef.current.scrollTop = activityScrollRef.current.scrollHeight;
    }
  }, [activityItems, showActivities]);

  useEffect(() => {
    setShowTagInput(false);
    setShowActivities(false);
    setActionMode(null);
    setTagInput("");
    setActionTitle("");
    setActionDueAt("");
    setActionType(taskTypes[0]?.value || "follow-up");
    setActionAssignee("me");
  }, [lead?.id, taskTypes]);

  const patchLead = async (patch, successMessage) => {
    if (!lead?.id) return;
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, patch);
      setActivityItems(Array.isArray(data?.tasks) ? data.tasks : []);
      onLeadUpdate?.(data);
      if (successMessage) toast.success(successMessage);
      return data;
    } catch (error) {
      console.error(">>> [ERRO-ATUALIZAR-LEAD]:", error?.response?.data || error);
      toast.error("Erro ao atualizar lead");
      return null;
    }
  };

  const handleSend = async (text, attachment) => {
    try {
      let data;
      const selectedChannel = responseChannels.find((item) => item.id === selectedResponseChannelId);
      const requiresResponseChannel = (lead.channel === "whatsapp" || isInstagramLead(lead)) && lead.remote_jid;
      if (requiresResponseChannel && !selectedChannel) {
        toast.error("Nenhum canal conectado disponível para resposta.");
        return;
      }
      if (selectedChannel && selectedChannel.canSend === false) {
        toast.error("O canal selecionado está desconectado.");
        return;
      }
      const payload = { lead_id: lead.id, content: text };
      if (attachment) {
        payload.media_url = absoluteMediaUrl(attachment.url);
        payload.media_type = attachment.media_type;
        if (!text) payload.content = `[${attachment.media_type}]`;
      }

      // Leads WhatsApp com remote_jid → rota pelo serviço Baileys nativo
      if ((lead.channel === "whatsapp" || isInstagramLead(lead)) && lead.remote_jid) {
        const res = await api.post("/whatsapp/send", {
          conversationId: lead.id,
          lead_id: lead.id,
          message: text,
          text,
          selectedChannelId: selectedChannel?.id,
          selectedAccountId: selectedChannel?.accountId,
          instanceId: selectedChannel?.instanceId,
          media_url: attachment?.url ? absoluteMediaUrl(attachment.url) : undefined,
          media_type: attachment?.media_type,
          fileName: attachment?.filename,
          filename: attachment?.filename,
          mimetype: attachment?.content_type,
          content_type: attachment?.content_type,
        });
        data = res.data;
      } else {
        // Instagram, email, Facebook ou WhatsApp com mídia → rota clássica
        const res = await api.post("/messages", payload);
        data = res.data;
      }

      // Deduplicação: o WebSocket pode disparar message.sent antes da resposta
      // da API chegar, adicionando a mensagem primeiro. Checamos por ID.
      setMessages((prev) => {
        if (data?.id && prev.find((m) => m.id === data.id)) return prev;
        return [...prev, data];
      });
    } catch (error) {
      console.error(">>> [ERRO-ENVIO-CHAT]:", error?.response?.data || error);
      toast.error(error?.response?.data?.detail || "Erro ao enviar mensagem");
    }
  };

  const handleDeleteLead = async () => {
    const leadName = lead?.name || "este lead";
    if (!lead?.id) return;
    if (!window.confirm(`Excluir ${leadName} permanentemente?`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      onLeadDelete?.(lead.id);
      onClose?.();
      toast.success("Lead excluido");
    } catch {
      toast.error("Erro ao excluir lead");
    }
  };

  const handleAddTag = async () => {
    const value = tagInput.trim();
    if (!value) return;
    const nextTags = Array.from(new Set([...(lead?.tags || []), value]));
    const updated = await patchLead({ tags: nextTags }, "Tag adicionada");
    if (updated) {
      setTagInput("");
      setShowTagInput(false);
    }
  };

  const handleRemoveTag = async (tag) => {
    const nextTags = (lead?.tags || []).filter((item) => item !== tag);
    await patchLead({ tags: nextTags });
  };

  const handleSaveAction = async () => {
    const title = actionTitle.trim();
    if (!title || savingActivity || savingActivityRef.current) return;

    if (actionMode === "reminder") {
      let dueIso = null;
      if (reminderMode === "date") {
        if (!reminderDate) {
          toast.error("Escolha uma data para o lembrete");
          return;
        }
        dueIso = new Date(`${reminderDate}T12:00:00`).toISOString();
      } else {
        const amount = Number(reminderRelativeValue) || 0;
        if (amount <= 0) {
          toast.error("Informe um prazo válido");
          return;
        }
        const base = new Date();
        if (reminderRelativeUnit === "days") base.setDate(base.getDate() + amount);
        else if (reminderRelativeUnit === "years") base.setFullYear(base.getFullYear() + amount);
        else base.setMonth(base.getMonth() + amount);
        dueIso = base.toISOString();
      }
      const reminderAssigneeName = actionAssignee === "me"
        ? (user?.name || "Eu")
        : (team.find((member) => member.id === actionAssignee)?.name || "Equipe");
      const reminderAssigneeId = actionAssignee === "me" ? user?.id : actionAssignee;
      const nextReminder = {
        id: crypto.randomUUID(),
        kind: "reminder",
        title,
        product: title,
        gift_recipient: reminderRecipient.trim() || null,
        task_type: "reminder",
        assignee: reminderAssigneeName,
        assignee_id: reminderAssigneeId || null,
        due_at: dueIso,
        completed: false,
        created_at: new Date().toISOString(),
      };
      try {
        savingActivityRef.current = true;
        setSavingActivity(true);
        const { data } = await api.post(`/leads/${lead.id}/activities`, nextReminder);
        setActivityItems(Array.isArray(data?.tasks) ? data.tasks : []);
        setShowActivities(true);
        onLeadUpdate?.(data);

        setActionMode(null);
        setActionTitle("");
        setReminderRecipient("");
        setReminderDate("");
        setReminderRelativeValue(3);
        setReminderRelativeUnit("months");
        setActionAssignee("me");
        toast.success("Lembrete agendado");
      } catch (error) {
        console.error(">>> [ERRO-SALVAR-LEMBRETE]:", error?.response?.data || error);
        toast.error("Erro ao salvar lembrete");
      } finally {
        savingActivityRef.current = false;
        setSavingActivity(false);
      }
      return;
    }

    const assigneeName = actionAssignee === "me"
      ? (user?.name || "Eu")
      : (team.find((member) => member.id === actionAssignee)?.name || "Equipe");
    const assignedToId = actionAssignee === "me" ? user?.id : actionAssignee;
    const nextTask = {
      id: crypto.randomUUID(),
      kind: actionMode || "task",
      title,
      task_type: actionMode === "task" ? actionType : "note",
      assignee: assigneeName,
      assignee_id: assignedToId || null,  // real user ID — backend uses this for task assignment
      due_at: actionMode === "task" && actionDueAt ? new Date(actionDueAt).toISOString() : null,
      completed: actionMode === "note",
      created_at: new Date().toISOString(),
    };
    try {
      savingActivityRef.current = true;
      setSavingActivity(true);
      // The backend add_activity endpoint is the SOLE creator of the unified task.
      // No additional POST /tasks call here — that was causing task duplication.
      const { data } = await api.post(`/leads/${lead.id}/activities`, nextTask);
      setActivityItems(Array.isArray(data?.tasks) ? data.tasks : []);
      setShowActivities(true);
      onLeadUpdate?.(data);

      setActionMode(null);
      setActionTitle("");
      setActionDueAt("");
      setActionType(taskTypes[0]?.value || "follow-up");
      setActionAssignee("me");
      toast.success(actionMode === "note" ? "Nota salva" : "Tarefa criada");
    } catch (error) {
      console.error(">>> [ERRO-SALVAR-ATIVIDADE]:", error?.response?.data || error);
      toast.error("Erro ao salvar atividade");
    } finally {
      savingActivityRef.current = false;
      setSavingActivity(false);
    }
  };

  const toggleTask = async (taskId) => {
    if (updatingTaskIds.includes(taskId)) return;
    const task = activityItems.find((t) => t.id === taskId);
    const nextTasks = (activityItems || []).map((t) =>
      t.id === taskId ? { ...t, completed: !t.completed } : t
    );
    setUpdatingTaskIds((prev) => [...prev, taskId]);
    const updated = await patchLead({ tasks: nextTasks });
    if (!updated) {
      console.error(">>> [ERRO-CONCLUIR-TAREFA]: falha ao alternar tarefa", taskId);
    }
    // Bidirectional sync: also update the unified task in /tasks collection
    if (task?.unified_task_id) {
      try {
        await api.patch(`/tasks/${task.unified_task_id}`, {
          status: task.completed ? "pending" : "completed",
        });
      } catch { /* silent — lead task was already updated */ }
    }
    setUpdatingTaskIds((prev) => prev.filter((id) => id !== taskId));
  };

  const removeTask = async (taskId) => {
    const nextTasks = (activityItems || []).filter((task) => task.id !== taskId);
    await patchLead({ tasks: nextTasks });
  };

  if (!lead) return null;

  const ch = isInstagramLead(lead) ? CHANNEL_ICONS.instagram : (CHANNEL_ICONS[lead.channel] || CHANNEL_ICONS.whatsapp);
  const ChIcon = ch.icon;
  const pendingTasks = activityItems.filter((task) => task.kind !== "note" && task.kind !== "reminder" && !task.completed);
  const orderedActivityItems = [...activityItems].reverse();
  const teamOptions = [{ id: "me", name: "Eu" }, ...team];
  const taskTypeLabels = Object.fromEntries(taskTypes.map((item) => [item.value, item.label]));
  const selectedResponseChannel = responseChannels.find((item) => item.id === selectedResponseChannelId);
  const requiresResponseChannel = (lead.channel === "whatsapp" || isInstagramLead(lead)) && lead.remote_jid;
  const canReplyViaSelectedChannel = !requiresResponseChannel || Boolean(selectedResponseChannel?.canSend);
  const displayPhone = lead.display_phone || null;
  const customerPhoneLabel = displayPhone || "Telefone não informado";
  const channelBadgeLabel = selectedResponseChannel
    ? compactChannelLabel(selectedResponseChannel)
    : loadingResponseChannels
      ? "CARREGANDO CANAIS"
      : "NENHUM CANAL CONECTADO";

  return (
    <AnimatePresence>
      {lead && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
            data-testid="chat-backdrop"
          />
          <motion.aside
            data-testid="chat-panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-y-0 right-0 w-full sm:w-[50vw] sm:min-w-[620px] sm:max-w-[860px] bg-slate-900 border-l border-slate-700 shadow-2xl z-50 flex flex-col"
          >
            <div className="px-4 py-3 border-b border-slate-800 bg-slate-900/95 backdrop-blur shrink-0">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="relative shrink-0">
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center text-sm font-bold text-slate-100">
                      {lead?.name?.slice?.(0, 2)?.toUpperCase?.() || "LD"}
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-slate-900 flex items-center justify-center border-2 border-slate-900">
                      <ChIcon className={`w-3 h-3 ${ch.color}`} />
                    </div>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-bold text-slate-50 truncate" style={{ fontFamily: "Outfit" }}>
                      {lead?.name || "Lead"}
                    </h3>
                    <div className="flex items-center gap-3 text-[11px] text-slate-400">
                      <span className={`flex items-center gap-1 ${!displayPhone ? "text-slate-600 italic" : ""}`}>
                        <Phone className="w-3 h-3" />
                        {customerPhoneLabel}
                      </span>
                      {lead.email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" /> {lead.email}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid="edit-lead-btn" onClick={() => setEditOpen(true)} title="Editar Lead" className="p-1.5 text-slate-400 hover:text-[#25D366] hover:bg-slate-800 rounded-md transition">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button data-testid="delete-lead-btn" onClick={handleDeleteLead} title="Excluir Lead" className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-md transition">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button data-testid="chat-close-btn" onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded-md transition">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-1.5 flex-wrap min-w-0">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex max-w-full items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md bg-[#25D366]/10 border border-[#25D366]/30 text-[#25D366] transition hover:bg-[#25D366]/15"
                    >
                      <Tag className="w-2.5 h-2.5 shrink-0" />
                      <span className="truncate">{channelBadgeLabel}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-72 border-slate-700 bg-slate-900 text-slate-100">
                    {responseChannels.length > 0 ? (
                      responseChannels.map((channel) => {
                        const selected = channel.id === selectedResponseChannelId;
                        const disabled = channel.canSend === false;
                        const channelPhone = formatChannelPhone(channel);
                        return (
                          <DropdownMenuItem
                            key={channel.id}
                            disabled={disabled}
                            onSelect={() => {
                              if (!disabled) setSelectedResponseChannelId(channel.id);
                            }}
                            className="flex items-center gap-2 rounded-md px-2 py-2 text-xs focus:bg-slate-800"
                          >
                            {channel.type === "instagram" ? (
                              <Instagram className={`h-4 w-4 ${disabled ? "text-slate-500" : "text-pink-400"}`} />
                            ) : (
                              <MessageCircle className={`h-4 w-4 ${disabled ? "text-slate-500" : "text-[#25D366]"}`} />
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="truncate font-medium">{channel.displayName || channel.name}</div>
                              <div className={`text-[10px] ${disabled ? "text-amber-300" : "text-slate-400"}`}>
                                {disabled ? "Canal desconectado" : channelPhone || channel.identifier || "Conectado"}
                              </div>
                            </div>
                            {selected && <Check className="h-4 w-4 text-[#25D366]" />}
                          </DropdownMenuItem>
                        );
                      })
                    ) : (
                      <div className="px-3 py-2 text-xs text-slate-400">
                        {loadingResponseChannels ? "Carregando canais..." : "Nenhum canal conectado"}
                      </div>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                {(lead.tags || []).map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-slate-800 border border-slate-700 text-slate-300">
                    {t}
                    <button type="button" onClick={() => handleRemoveTag(t)} className="text-slate-500 hover:text-red-400">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {!showTagInput ? (
                  <button
                    type="button"
                    onClick={() => setShowTagInput(true)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium text-slate-500 hover:text-slate-200 hover:bg-slate-800 transition"
                  >
                    <Hash className="w-3 h-3" />
                    adicionar tags
                  </button>
                ) : (
                  <div className="inline-flex items-center gap-1">
                    <input
                      autoFocus
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddTag();
                        if (e.key === "Escape") {
                          setTagInput("");
                          setShowTagInput(false);
                        }
                      }}
                      placeholder="Nova tag"
                      className="w-28 bg-slate-800 border border-slate-700 rounded-md px-2 py-1 text-[10px] text-slate-100 outline-none focus:border-[#25D366]"
                    />
                    <button onClick={handleAddTag} className="p-1 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-100">
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                )}
                {(lead?.value || 0) > 0 && (
                  <span className="text-xs font-bold text-[#25D366] ml-auto">
                    R$ {lead?.value?.toLocaleString?.("pt-BR") || "0"}
                  </span>
                )}
              </div>

              <div className="mt-3 flex items-center justify-between gap-2">
                <QuickActions lead={lead} googleConnected={channels?.google?.calendar_connected} onPickMessage={(text) => setPrefill(text)} />
                <button
                  type="button"
                  onClick={() => setShowActivities((value) => !value)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium transition ${
                    pendingTasks.length > 0
                      ? "text-amber-300 bg-amber-500/10 border border-amber-500/20"
                      : "text-slate-400 hover:text-slate-100 hover:bg-slate-800"
                  }`}
                >
                  <ListTodo className="w-3.5 h-3.5" />
                  {pendingTasks.length > 0 ? `${pendingTasks.length} Pendente${pendingTasks.length > 1 ? "s" : ""}` : "Atividades"}
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-0 flex flex-col bg-gradient-to-b from-slate-900 to-slate-950">
              {showActivities && (
                <div className="shrink-0 px-4 pt-3">
                  <div
                    ref={activityScrollRef}
                    className="max-h-52 overflow-y-auto space-y-2 rounded-xl bg-slate-950/70 border border-slate-800 p-3"
                  >
                    {orderedActivityItems.length === 0 ? (
                      <div className="text-[11px] text-slate-500">Sem notas ou tarefas registradas ainda.</div>
                    ) : (
                      orderedActivityItems.map((task) => (
                        <div key={task.id} className="flex items-start gap-2 px-2 py-2 rounded-lg bg-slate-900 border border-slate-800">
                          {task.kind === "note" ? (
                            <div className="mt-0.5">
                              <StickyNote className="w-4 h-4 text-blue-400" />
                            </div>
                          ) : task.kind === "reminder" ? (
                            <div className="mt-0.5">
                              <Gift className="w-4 h-4 text-pink-400" />
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => toggleTask(task.id)}
                              disabled={updatingTaskIds.includes(task.id)}
                              className="mt-0.5 text-slate-400 hover:text-[#25D366] disabled:opacity-50"
                            >
                              {task.completed ? <CheckCircle2 className="w-4 h-4 text-[#25D366]" /> : <Circle className="w-4 h-4" />}
                            </button>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-slate-100">{task.title}</div>
                            <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-3">
                              <span>{formatActivityLabel(task, taskTypeLabels)}</span>
                              {task.kind === "reminder" && task.gift_recipient && <span>{`Presente para ${task.gift_recipient}`}</span>}
                              {task.assignee && <span>{task.kind === "reminder" ? `Resp.: ${task.assignee}` : `Criado por ${task.assignee}`}</span>}
                              {formatActivityTimestamp(task) && <span>{formatActivityTimestamp(task)}</span>}
                            </div>
                          </div>
                          {task.kind !== "note" && task.kind !== "reminder" && !task.completed && (
                            <button
                              type="button"
                              onClick={() => toggleTask(task.id)}
                              disabled={updatingTaskIds.includes(task.id)}
                              className="shrink-0 px-2 py-1 rounded-md border border-slate-700 text-[10px] font-medium text-slate-300 hover:text-[#25D366] hover:border-[#25D366]/40 disabled:opacity-50"
                            >
                              Concluir
                            </button>
                          )}
                          <button onClick={() => removeTask(task.id)} className="text-slate-500 hover:text-red-400">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-2">
                {loading ? (
                  <div className="flex justify-center py-6">
                    <div className="w-6 h-6 border-2 border-[#25D366] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (messages || []).length === 0 ? (
                  <div className="text-center py-10 text-sm text-slate-500">
                    Nenhuma mensagem ainda. Envie a primeira!
                  </div>
                ) : (
                  (messages || []).map((m, i) => <MessageBubble key={m.id} message={m} index={i} />)
                )}
              </div>
            </div>

            <div className="shrink-0 border-t border-slate-800 bg-slate-900">
              <div className="px-3 pt-2">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setActionMode((current) => current === "note" ? null : "note")}
                    className={`px-2.5 py-1.5 rounded-md text-[11px] font-medium transition ${
                      actionMode === "note" ? "bg-blue-500/15 text-blue-300 border border-blue-500/20" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800"
                    }`}
                  >
                    Nota
                  </button>
                  <button
                    type="button"
                    onClick={() => setActionMode((current) => current === "task" ? null : "task")}
                    className={`px-2.5 py-1.5 rounded-md text-[11px] font-medium transition ${
                      actionMode === "task" ? "bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/20" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800"
                    }`}
                  >
                    Tarefa
                  </button>
                  <button
                    type="button"
                    onClick={() => setActionMode((current) => current === "reminder" ? null : "reminder")}
                    className={`px-2.5 py-1.5 rounded-md text-[11px] font-medium transition ${
                      actionMode === "reminder" ? "bg-pink-500/15 text-pink-300 border border-pink-500/20" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800"
                    }`}
                  >
                    Lembrete futuro
                  </button>
                </div>

                {actionMode === "reminder" && (
                  <div className="mt-2 flex flex-col gap-2 rounded-xl bg-slate-950 border border-slate-800 p-2">
                    <div className="flex items-center gap-2">
                      <input
                        value={actionTitle}
                        onChange={(e) => setActionTitle(e.target.value)}
                        placeholder="Produto/serviço vendido (ex: Perfume XPTO)"
                        className="flex-[1.6] min-w-0 bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-100 outline-none focus:border-[#25D366]"
                      />
                      <input
                        value={reminderRecipient}
                        onChange={(e) => setReminderRecipient(e.target.value)}
                        placeholder="Presente para (opcional, ex: esposa)"
                        className="flex-1 min-w-0 bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-100 outline-none focus:border-[#25D366]"
                      />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="w-[112px] shrink-0">
                        <Select value={reminderMode} onValueChange={setReminderMode}>
                          <SelectTrigger className="h-9 bg-slate-900 border-slate-700 text-slate-100">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                            <SelectItem value="relative">Prazo</SelectItem>
                            <SelectItem value="date">Data específica</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {reminderMode === "date" ? (
                        <input
                          type="date"
                          value={reminderDate}
                          onChange={(e) => setReminderDate(e.target.value)}
                          className="w-[150px] shrink-0 bg-slate-900 border border-slate-700 rounded-md px-2 py-2 text-xs text-slate-100 outline-none focus:border-[#25D366]"
                        />
                      ) : (
                        <>
                          <input
                            type="number"
                            min="1"
                            value={reminderRelativeValue}
                            onChange={(e) => setReminderRelativeValue(e.target.value)}
                            className="w-[60px] shrink-0 bg-slate-900 border border-slate-700 rounded-md px-2 py-2 text-xs text-slate-100 outline-none focus:border-[#25D366]"
                          />
                          <div className="w-[100px] shrink-0">
                            <Select value={reminderRelativeUnit} onValueChange={setReminderRelativeUnit}>
                              <SelectTrigger className="h-9 bg-slate-900 border-slate-700 text-slate-100">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                                <SelectItem value="days">dias</SelectItem>
                                <SelectItem value="months">meses</SelectItem>
                                <SelectItem value="years">anos</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}
                      <div className="w-[92px] shrink-0">
                        <Select value={actionAssignee} onValueChange={setActionAssignee}>
                          <SelectTrigger className="h-9 bg-slate-900 border-slate-700 text-slate-100">
                            <SelectValue placeholder="Resp." />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                            {teamOptions.map((member) => (
                              <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <button
                        type="button"
                        onClick={handleSaveAction}
                        disabled={savingActivity}
                        className="shrink-0 ml-auto px-3 py-2 rounded-md bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 text-xs font-semibold"
                      >
                        {savingActivity ? "Salvando..." : "Agendar lembrete"}
                      </button>
                    </div>
                  </div>
                )}

                {actionMode && actionMode !== "reminder" && (
                  <div className="mt-2 flex items-center gap-2 rounded-xl bg-slate-950 border border-slate-800 p-2">
                    {actionMode === "task" && (
                      <HelpTooltip
                        text="Tipo define o rótulo/ícone da tarefa (ex: Ligação, Reunião). Resp. é quem deve executá-la -- se for diferente de você, a pessoa recebe uma notificação. Data/hora é o prazo: a tarefa aparece em /tasks e fica marcada como atrasada depois que passar."
                        className="shrink-0"
                      />
                    )}
                    {actionMode === "task" && (
                      <div className="w-[120px] shrink-0">
                        <Select value={actionType} onValueChange={setActionType}>
                          <SelectTrigger className="h-9 bg-slate-900 border-slate-700 text-slate-100">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                            {taskTypes.map((option) => (
                              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <input
                      value={actionTitle}
                      onChange={(e) => setActionTitle(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSaveAction()}
                      placeholder={actionMode === "note" ? "Escreva uma nota interna" : "Descreva a tarefa"}
                      className="flex-[1.8] min-w-0 bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-100 outline-none focus:border-[#25D366]"
                    />
                    {actionMode === "task" && (
                      <>
                        <div className="w-[92px] shrink-0">
                          <Select value={actionAssignee} onValueChange={setActionAssignee}>
                            <SelectTrigger className="h-9 bg-slate-900 border-slate-700 text-slate-100">
                              <SelectValue placeholder="Resp." />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                              {teamOptions.map((member) => (
                                <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <input
                          type="datetime-local"
                          value={actionDueAt}
                          onChange={(e) => setActionDueAt(e.target.value)}
                          className="w-[154px] shrink-0 bg-slate-900 border border-slate-700 rounded-md px-2 py-2 text-xs text-slate-100 outline-none focus:border-[#25D366]"
                        />
                        <button
                          type="button"
                          onClick={handleSaveAction}
                          disabled={savingActivity}
                          className="shrink-0 px-3 py-2 rounded-md bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 text-xs font-semibold"
                        >
                          {savingActivity ? "Salvando..." : "Salvar"}
                        </button>
                      </>
                    )}
                    {actionMode === "note" && (
                      <button
                        type="button"
                        onClick={handleSaveAction}
                        disabled={savingActivity}
                        className="shrink-0 px-3 py-2 rounded-md bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 text-xs font-semibold"
                      >
                        {savingActivity ? "Salvando..." : "Salvar"}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <InputArea
                onSend={handleSend}
                prefill={prefill}
                onPrefillConsumed={() => setPrefill(null)}
                disabled={!canReplyViaSelectedChannel || (requiresResponseChannel && responseChannels.length === 0)}
              />
            </div>
          </motion.aside>

          <EditLeadModal open={editOpen} lead={lead} onClose={() => setEditOpen(false)} onSaved={(updated) => onLeadUpdate?.(updated)} />
        </>
      )}
    </AnimatePresence>
  );
}
